import * as React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { UserConditionModel } from "../../../models/user/userConditionModel";
import { ConditionUnitRangeModel } from '../../../models/user/conditionUnitRangeModel';
import * as HelperFunctions from '../../../common/functions/helperFunctions';
import { UserConditionType, GenderType } from '../../../common/enums';
import color from '@material-ui/core/colors/deepOrange';
import { green } from '@material-ui/core/colors';
import Translate from '../../../common/functions/translate';

declare module '@nivo/bar'

class BarModel {
    public conditionDate?: string;
    public value: number;
    public valueColor: string;
    public secondValue?: number;
    public secondValueColor?: string;
}

let minBorder: any;
let maxBorder: any;

const barData = (userCondition: UserConditionModel, lang: string, typeRanges: ConditionUnitRangeModel) => {
    let label = userCondition.isSystemType ? HelperFunctions.getEnumLabel(userCondition.userConditionTypeId, UserConditionType, true) +
        ' (' + userCondition.userConditionUnit + ')' + '' : userCondition.personalCondition + '';

    let data: Array<BarModel> = new Array<BarModel>(0);
    let sortedItems = userCondition.userConditionTypes.sort((a, b) => new Date(a.conditionDate).getTime() - new Date(b.conditionDate).getTime());
    for (let cond of sortedItems) {
        let ucond: BarModel = new BarModel();
        ucond.conditionDate = HelperFunctions.fomatDateTimeSecondsAsStringWithLang(new Date(cond.conditionDate), lang);
        if (userCondition.isSystemType && userCondition.userConditionTypeId == UserConditionType.BloodPressure) {
            let bPressure = cond.userConditionValue.split('/');
            ucond.value = parseFloat(bPressure[0]);
            ucond.secondValue = parseFloat(bPressure[1]);
            ucond.valueColor = "#7bc87b";
            ucond.secondValueColor = "#7bc87b";

            if (typeRanges &&
                ((typeRanges.minValue && typeRanges.minValue > bPressure[0]) || (typeRanges.maxValue && typeRanges.maxValue < bPressure[0]))) {
                ucond.valueColor = "#ff4d4d";
            }

            if (typeRanges &&
                ((typeRanges.secondMinValue && typeRanges.secondMinValue > bPressure[1]) || (typeRanges.secondMaxValue && typeRanges.secondMaxValue < bPressure[1]))) {
                ucond.secondValueColor = "#ff4d4d";
            }
        }
        else {
            ucond.value = parseFloat(cond.userConditionValue)
            ucond.valueColor = "#7bc87b";
            if (typeRanges &&
                ((typeRanges.minValue && typeRanges.minValue > cond.userConditionValue) || (typeRanges.maxValue && typeRanges.maxValue < cond.userConditionValue))) {
                ucond.valueColor = "#ff4d4d";
            }
        }
        data.push(ucond);
    }

    return data;
}

const getMinAndMax = (userCondition: UserConditionModel, isMin: boolean, minMarker: number, maxMarker: number, isThereMarkers: boolean) => {
    let val: number = 0;
    if (userCondition.isSystemType && userCondition.userConditionTypeId == UserConditionType.BloodPressure) {
        if (isThereMarkers) {
            val = isMin ? minMarker : maxMarker
        }
        else {
            let blPressure = userCondition.userConditionTypes[0].userConditionValue.split('/');
            val = isMin ? +blPressure[1] : +blPressure[0];
        }
        for (let uType of userCondition.userConditionTypes) {
            let bPressure = uType.userConditionValue.split('/');
            if (isMin) {
                val = +bPressure[1] < val ? +bPressure[1] : val;
            }
            else {
                val = +bPressure[0] > val ? +bPressure[0] : val;
            }
        }
    }
    else {
        if (isThereMarkers) {
            val = isMin ? minMarker : maxMarker
        }
        else {
            val = +userCondition.userConditionTypes[0].userConditionValue;
        }
        for (let uType of userCondition.userConditionTypes) {
            if (isMin) {
                val = +uType.userConditionValue < val ? +uType.userConditionValue : val;
            }
            else {
                val = +uType.userConditionValue > val ? +uType.userConditionValue : val;
            }
        }

    }
    return val;
}

const UserConditionBars = (props) => {
    let typeRange: ConditionUnitRangeModel = props.typeRanges;
    let label = props.userCondition.isSystemType ? HelperFunctions.getEnumLabel(props.userCondition.userConditionTypeId, UserConditionType, true) +
        ' (' + props.userCondition.userConditionUnit + ')' + '' : props.userCondition.personalCondition + '';

    let markers = new Array<any>(0);
    let minMarker: number = 0;
    let maxMarker: number = 0;

    if (typeRange) {
        minMarker = +typeRange.minValue;
        maxMarker = +typeRange.maxValue;

        if (typeRange.minValue) {
            markers.push({
                axis: "y", value: +typeRange.minValue,
                lineStyle: { stroke: '#0aa5ff', strokeDasharray: "12, 6" }, legend: "min: " + typeRange.minValue, textStyle: { fontSize: 10 }
            });
            if (+typeRange.minValue < minMarker) {
                minMarker = +typeRange.minValue;
            }
        }
        if (typeRange.maxValue) {
            markers.push({
                axis: "y", value: +typeRange.maxValue,
                lineStyle: { stroke: '#0aa5ff', strokeDasharray: "12, 6" }, legend: "max: " + typeRange.maxValue, textStyle: { fontSize: 10 }
            });
            if (+typeRange.maxValue > maxMarker) {
                maxMarker = +typeRange.maxValue;
            }
        }

        if (typeRange.secondMinValue) {
            markers.push({
                axis: "y", value: +typeRange.secondMinValue,
                lineStyle: { stroke: '#34675c', strokeDasharray: "12, 6" }, legend: "min: " + typeRange.secondMinValue, legendPosition: 'bottom-right', textStyle: { fontSize: 10 }
            });
            if (+typeRange.secondMinValue < minMarker) {
                minMarker = +typeRange.secondMinValue;
            }
        }
        if (typeRange.secondMaxValue) {
            markers.push({
                axis: "y", value: +typeRange.secondMaxValue,
                lineStyle: { stroke: '#34675c', strokeDasharray: "12, 6" }, legend: "max: " + typeRange.secondMaxValue, legendPosition: 'bottom-right', textStyle: { fontSize: 10 }
            });
            if (+typeRange.secondMaxValue > maxMarker) {
                maxMarker = +typeRange.secondMaxValue;
            }
        }
    }

    let minVal: number = getMinAndMax(props.userCondition, true, minMarker, maxMarker, markers && markers.length > 0);
    let maxVal: number = getMinAndMax(props.userCondition, false, minMarker, maxMarker, markers && markers.length > 0);

    let delta = maxVal - minVal;

    minBorder = minVal != 0 ? minVal - (delta / 5) : null;
    maxBorder = maxVal != 0 ? maxVal + (delta / 2) : null;

    let data = barData(props.userCondition, props.lang, props.typeRanges);
    let keys: string[] = [];
    if (props.userCondition.isSystemType && props.userCondition.userConditionTypeId == UserConditionType.BloodPressure) {
        keys = ["value", "secondValue"];
    }
    else {
        keys = ["value"];
    }

    let axisBottom: any = {
        "tickSize": 5,
        "tickPadding": 15,
        "tickRotation": 0,
        "legend": "",
        "legendOffset": 46,
        "legendPosition": "middle",
        renderTick: (data) => {
            const { value, opacity, textBaseline, textAnchor, textX, textY, x, y } = data
            return (

                <g key={value}
                    transform={`translate(${x},${y})`}
                    style={{ opacity }}
                >
                    <text
                        alignmentBaseline={textBaseline}
                        style={{ fontSize: 10 }}
                        textAnchor={textAnchor}
                        transform={`translate(${textX},${textY})`}
                    >
                        {getTspanGroups(value, 10, 3)}
                    </text>
                </g>
            )
        }
    }

    return (
        <ResponsiveBar
            data={data}
            groupMode="grouped"
            indexBy="conditionDate"
            margin={{
                "top": 0,
                "right": 10,
                "bottom": 50,
                "left": 30
            }}
            keys={keys}
            padding={0.3}
            colors={({ id, data }) => data[`${id}Color`]}
            // minValue={minVal != 0 ? minVal - (delta / 5) : "auto"}
            maxValue={maxBorder != null ? maxBorder : "auto"}
            markers={markers}
            axisTop={null}
            axisRight={null}
            axisBottom={axisBottom}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={"white"}
            // defs={[
            //     {
            //         id: 'lines-green',
            //         type: 'patternLines',
            //         background: 'inherit',
            //         color: '#68a868',
            //         rotation: -45,
            //         lineWidth: 4,
            //         spacing: 10
            //     },
            //     {
            //         id: 'lines-red',
            //         type: 'patternLines',
            //         background: 'inherit',
            //         color: '#b23b3b',
            //         rotation: -45,
            //         lineWidth: 4,
            //         spacing: 10
            //     }
            // ]}
            // fill={[
            //     {
            //         match: d => {
            //             console.log(d)
            //             return d.key.toString().includes("secondValue") && d.color === '#7bc87b'
            //         }, id: 'lines-green'
            //     },
            //     {
            //         match: d => {
            //             console.log(d)
            //             return d.key.toString().includes("secondValue") && d.color === '#ff4d4d'
            //         }, id: 'lines-red'
            //     }
            // ]}
            animate={true}
            motionStiffness={90}
            motionDamping={15}
            tooltip={({ id, value, color }) => {
                let tooltip = <strong>
                    {label}: <span style={{ color }}>{value}</span>
                </strong>
                if (props.userCondition.isSystemType && props.userCondition.userConditionTypeId == UserConditionType.BloodPressure) {
                    if (id == "value") {
                        tooltip = <strong>
                            {Translate('TopNumber') + ' (' + props.userCondition.userConditionUnit + ')'}: <span style={{ color }}>{value}</span>
                        </strong>
                    }
                    else {
                        tooltip = <strong>
                            {Translate('BottomNumber') + ' (' + props.userCondition.userConditionUnit + ')'}: <span style={{ color }}>{value}</span>
                        </strong>
                    }
                }
                return tooltip;
            }}
        />
    )

}

const getTspanGroups = (value: string, maxLineLength: number, maxLines: number = 2) => {
    const words = value.split(' ')

    type linesAcc = {
        lines: string[],
        currLine: string
    }

    //reduces the words into lines of maxLineLength
    const assembleLines: linesAcc = words.reduce((acc: linesAcc, word: string) => {
        //if the current line isn't empty and the word + current line is larger than the allowed line size, create a new line and update current line
        if ((word + acc.currLine).length > maxLineLength && acc.currLine !== '') {
            return {
                lines: acc.lines.concat([acc.currLine]),
                currLine: word
            }
        }
        //otherwise add the word to the current line
        return {
            ...acc,
            currLine: acc.currLine + ' ' + word
        }

    }, { lines: [], currLine: '' })

    //add the ending state of current line (the last line) to lines
    const allLines = assembleLines.lines.concat([assembleLines.currLine])

    //for now, only take first 2 lines due to tick spacing and possible overflow
    const lines = allLines.slice(0, maxLines)
    let children: JSX.Element[] = []
    let dy = 0

    lines.forEach((lineText, i) => {
        children.push(
            <tspan x={0} dy={dy} key={i}>
                {
                    // if on the second line, and that line's length is within 3 of the max length, add ellipsis
                    (1 === i && allLines.length > 2) ? lineText.slice(0, maxLineLength - 3) + '...' : lineText
                }
            </tspan>
        )
        //increment dy to render next line text below
        dy += 15
    });

    return children
}

export default UserConditionBars;